import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { dashboardTheme } from '../styles/dashboardTheme';
import WidgetInfoTooltip from './WidgetInfoTooltip';
import { format } from 'date-fns';

/**
 * CheckInHistoryWidget Component
 * Shows recent check-in history with mood and anxiety trends
 * Supports both legacy (mood/anxiety) and template-based (SUDS/Presence) check-ins
 */
const CheckInHistoryWidget = ({ checkIns = [], onViewHistory }) => {
  // Filter out invalid check-ins - accept both legacy AND template-based
  const validCheckIns = checkIns.filter(c =>
    // Legacy check-ins have moodRating
    (c.moodRating !== undefined && c.moodRating !== null) ||
    // Template-based check-ins have sudsRating or presenceRating or dynamicResponses
    (c.sudsRating !== undefined && c.sudsRating !== null) ||
    (c.presenceRating !== undefined && c.presenceRating !== null) ||
    (c.dynamicResponses && c.dynamicResponses.length > 0)
  );
  const recentCheckIns = validCheckIns.slice(0, 5);

  // Determine if we have mostly template-based or legacy check-ins
  const templateCheckIns = recentCheckIns.filter(c => c.sudsRating != null || c.presenceRating != null || c.dynamicResponses?.length > 0);
  const legacyCheckIns = recentCheckIns.filter(c => c.moodRating != null);
  const isTemplateMode = templateCheckIns.length > legacyCheckIns.length;

  // Calculate averages based on check-in type
  const averages = recentCheckIns.length > 0 ? (isTemplateMode ? {
    primary: templateCheckIns.reduce((sum, c) => sum + (c.sudsRating || 0), 0) / (templateCheckIns.length || 1),
    secondary: templateCheckIns.reduce((sum, c) => sum + (c.presenceRating || 0), 0) / (templateCheckIns.length || 1),
    primaryLabel: 'SUDS',
    secondaryLabel: 'Presence',
    primaryMax: 10,
    secondaryMax: 10
  } : {
    primary: legacyCheckIns.reduce((sum, c) => sum + (c.moodRating || 0), 0) / (legacyCheckIns.length || 1),
    secondary: legacyCheckIns.reduce((sum, c) => sum + (c.anxietyRating || 0), 0) / (legacyCheckIns.length || 1),
    primaryLabel: 'Mood',
    secondaryLabel: 'Anxiety',
    primaryMax: 5,
    secondaryMax: 5
  }) : { primary: 0, secondary: 0, primaryLabel: 'Mood', secondaryLabel: 'Anxiety', primaryMax: 5, secondaryMax: 5 };

  // Get emoji for mood rating (1-5 scale)
  const getMoodEmoji = (rating) => {
    const emojiMap = {
      1: '😢',
      2: '😔',
      3: '😐',
      4: '🙂',
      5: '😊'
    };
    return emojiMap[Math.round(rating)] || '😐';
  };

  // Get emoji for SUDS rating (0-10 scale, inverted - lower is better)
  const getSudsEmoji = (rating) => {
    if (rating <= 2) return '😌';
    if (rating <= 4) return '🙂';
    if (rating <= 6) return '😐';
    if (rating <= 8) return '😰';
    return '😫';
  };

  // Get appropriate emoji based on check-in type
  const getCheckInEmoji = (checkIn) => {
    if (checkIn.sudsRating != null) {
      return getSudsEmoji(checkIn.sudsRating);
    }
    if (checkIn.moodRating != null) {
      return getMoodEmoji(checkIn.moodRating);
    }
    return '📋';
  };

  const getAnxietyColor = (rating, max = 5) => {
    const normalized = (rating / max) * 5;
    if (normalized <= 2) return '#66BB6A';
    if (normalized <= 3) return '#FFA726';
    return '#EF5350';
  };

  // Get primary/secondary values for a check-in
  const getCheckInValues = (checkIn) => {
    if (checkIn.sudsRating != null || checkIn.presenceRating != null) {
      return {
        primary: checkIn.sudsRating,
        secondary: checkIn.presenceRating,
        primaryLabel: 'SUDS',
        secondaryLabel: 'Presence',
        primaryMax: 10,
        secondaryMax: 10
      };
    }
    return {
      primary: checkIn.moodRating,
      secondary: checkIn.anxietyRating,
      primaryLabel: 'Mood',
      secondaryLabel: 'Anxiety',
      primaryMax: 5,
      secondaryMax: 5
    };
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: dashboardTheme.shadows.medium,
        transition: dashboardTheme.animations.transition,
        '&:hover': {
          boxShadow: dashboardTheme.shadows.large
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Check-in History
          </Typography>
          <WidgetInfoTooltip id="checkInHistory" />
        </Box>
        {recentCheckIns.length > 0 && (
          <Chip
            icon={<TrendingUpIcon sx={{ fontSize: '0.9rem' }} />}
            label={`${recentCheckIns.length} this week`}
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        )}
      </Box>

      {recentCheckIns.length > 0 ? (
        <Box sx={{ flex: 1 }}>
          {/* Averages Display - adapts to check-in type */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            mb: 2
          }}>
            <Box sx={{
              p: 1.5,
              background: isTemplateMode
                ? 'linear-gradient(135deg, rgba(239, 83, 80, 0.05) 0%, rgba(239, 83, 80, 0.02) 100%)'
                : 'linear-gradient(135deg, rgba(255, 140, 0, 0.05) 0%, rgba(255, 140, 0, 0.02) 100%)',
              borderRadius: '12px',
              border: isTemplateMode ? '1px solid rgba(239, 83, 80, 0.1)' : '1px solid rgba(255, 140, 0, 0.1)'
            }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Avg {averages.primaryLabel}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {averages.primary.toFixed(1)}/{averages.primaryMax}
                </Typography>
                {!isTemplateMode && (
                  <Typography variant="h6">
                    {getMoodEmoji(averages.primary)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{
              p: 1.5,
              background: isTemplateMode
                ? 'linear-gradient(135deg, rgba(102, 187, 106, 0.05) 0%, rgba(102, 187, 106, 0.02) 100%)'
                : `linear-gradient(135deg, ${getAnxietyColor(averages.secondary, averages.secondaryMax)}15 0%, ${getAnxietyColor(averages.secondary, averages.secondaryMax)}05 100%)`,
              borderRadius: '12px',
              border: isTemplateMode
                ? '1px solid rgba(102, 187, 106, 0.1)'
                : `1px solid ${getAnxietyColor(averages.secondary, averages.secondaryMax)}20`
            }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Avg {averages.secondaryLabel}
              </Typography>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                color: isTemplateMode ? '#66BB6A' : getAnxietyColor(averages.secondary, averages.secondaryMax)
              }}>
                {averages.secondary.toFixed(1)}/{averages.secondaryMax}
              </Typography>
            </Box>
          </Box>

          {/* Recent Check-ins List */}
          <List sx={{ py: 0 }}>
            {recentCheckIns.slice(0, 3).map((checkIn, index) => {
              const values = getCheckInValues(checkIn);
              return (
              <ListItem
                key={checkIn._id || index}
                sx={{
                  px: 2,
                  py: 1,
                  background: index % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
                  borderRadius: '8px',
                  mb: 0.5
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {format(new Date(checkIn.createdAt), 'MMM d')}
                      </Typography>
                      <Typography variant="body2">
                        {getCheckInEmoji(checkIn)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      {values.primary != null && (
                        <Chip
                          label={`${values.primaryLabel}: ${values.primary}/${values.primaryMax}`}
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.7rem',
                            background: values.primaryLabel === 'SUDS' ? 'rgba(239, 83, 80, 0.1)' : 'rgba(255, 140, 0, 0.1)',
                            color: values.primaryLabel === 'SUDS' ? '#EF5350' : dashboardTheme.colors.primary.main
                          }}
                        />
                      )}
                      {values.secondary != null && (
                        <Chip
                          label={`${values.secondaryLabel}: ${values.secondary}/${values.secondaryMax}`}
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '0.7rem',
                            background: values.secondaryLabel === 'Presence' ? 'rgba(102, 187, 106, 0.1)' : `${getAnxietyColor(values.secondary, values.secondaryMax)}15`,
                            color: values.secondaryLabel === 'Presence' ? '#66BB6A' : getAnxietyColor(values.secondary, values.secondaryMax)
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
            })}
          </List>
        </Box>
      ) : (
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'text.secondary'
          }}
        >
          <HistoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant="body2" align="center" gutterBottom>
            No check-in history yet
          </Typography>
          <Typography variant="caption" align="center">
            Start tracking your mood daily
          </Typography>
        </Box>
      )}

      <Button
        variant={recentCheckIns.length > 0 ? "outlined" : "contained"}
        fullWidth
        startIcon={<HistoryIcon />}
        onClick={onViewHistory}
        sx={{ 
          mt: 2,
          borderRadius: '12px',
          background: recentCheckIns.length > 0 ? 'transparent' : dashboardTheme.colors.secondary.gradient,
          '&:hover': {
            background: recentCheckIns.length > 0 ? dashboardTheme.colors.secondary.main + '10' : dashboardTheme.colors.secondary.dark
          }
        }}
      >
        {recentCheckIns.length > 0 ? 'View Full History' : 'Learn About Check-ins'}
      </Button>
    </Paper>
  );
};

export default CheckInHistoryWidget;